import {
  type SearcherDocumentSearchFilter,
  SearcherDocumentSearchFilterOperator,
} from 'src/api/generated';
import {
  type Filter,
  type FilterFieldOptions,
} from '../../types/filters.types';
import { isFilterValue } from './values.util';
import {
  convertDateValueToISO,
  convertDateValueToEndOfDay,
  isDateOnlyFormat,
} from './date-conversion.util';

/**
 * Expands a date eq filter to a date range (gte + lt)
 * This allows matching all timestamps within a day in the user's timezone
 */
const expandDateEqToRange = (
  filter: Filter
): SearcherDocumentSearchFilter[] | null => {
  const startOfDay = convertDateValueToISO(filter.value);
  const startOfNextDay = convertDateValueToEndOfDay(filter.value);

  if (!startOfDay || !startOfNextDay) {
    return null;
  }

  return [
    {
      field: filter.fieldOptionId,
      operator: SearcherDocumentSearchFilterOperator.GTE,
      value: startOfDay,
      or: filter.isOr,
    },
    {
      field: filter.fieldOptionId,
      operator: SearcherDocumentSearchFilterOperator.LT,
      value: startOfNextDay,
      or: filter.isOr,
    },
  ];
};

/**
 * Converts a date value to ISO format if applicable
 */
const convertDateValue = (filter: Filter): Filter['value'] => {
  const isoValue = convertDateValueToISO(filter.value);
  return isoValue ?? filter.value;
};

/**
 * Checks if a filter should be expanded to a date range
 */
const shouldExpandToDateRange = (
  filter: Filter,
  fieldOption?: FilterFieldOptions
): boolean => {
  return (
    fieldOption?.type === 'date' &&
    !fieldOption.includeTime &&
    filter.operator === SearcherDocumentSearchFilterOperator.EQ &&
    isDateOnlyFormat(filter.value)
  );
};

/**
 * Convert a FilterField to one or more API filters
 *
 * When a date field uses the 'eq' operator with a date-only value (e.g., "2025-10-08"),
 * it expands to a range query (gte + lt) to match all timestamps in that day in the user's timezone.
 *
 * @param filter - The filter to convert
 * @param fieldsOptions - Field definitions to determine field types for proper conversion
 * @returns Array of API filters (usually 1, but 2 for date eq filters)
 */
export const toApiFilter = (
  filter: Filter,
  fieldsOptions: FilterFieldOptions[] = []
): SearcherDocumentSearchFilter[] => {
  const fieldOption = fieldsOptions.find((f) => f.id === filter.fieldOptionId);

  // Special case: Expand date eq filters to date range (gte + lt)
  if (shouldExpandToDateRange(filter, fieldOption)) {
    const rangeFilters = expandDateEqToRange(filter);
    if (rangeFilters) {
      return rangeFilters;
    }
  }

  // Standard case: Convert date values to ISO format and return single filter
  const convertedValue =
    fieldOption?.type === 'date' && filter.value
      ? convertDateValue(filter)
      : filter.value;

  return [
    {
      field: filter.fieldOptionId,
      operator: filter.operator,
      or: filter.isOr,
      value: convertedValue,
    },
  ];
};

/**
 * Convert an API filter to a FilterField
 */
export const fromApiFilter = (
  filter: SearcherDocumentSearchFilter,
  id: string
): Filter => {
  return {
    id,
    fieldOptionId: filter.field,
    operator: filter.operator,
    value: isFilterValue(filter.value) ? filter.value : undefined,
    isOr: filter.or,
  };
};
